import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthContext } from 'contexts/auth-context';
import { hexToAscii } from 'utils/hex';
import { PermissionsTypes } from 'model/role-permissions';
import { items } from '../layouts/dashboard/config';
import { userHasPermissionOnResource } from '../utils/user-permissions';
import ForbiddenPage from '../pages/forbidden';

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, user } = useAuthContext();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      const asPath = location.pathname + location.search + location.hash;
      const continueUrl = asPath !== '/' ? `?continueUrl=${encodeURIComponent(asPath)}` : '';
      navigate(`/auth/login${continueUrl}`, { replace: true });
    } else {
      if (location.pathname === '/' && user && user.roles && !user.roles.includes('Inactive')) {
        const cognitoState = searchParams.get('state');
        if (cognitoState && cognitoState.includes('-')) {
          // Cognito's state has the following format: <COGNITO_STATE>-<CUSTOM_STATE>,
          // with both states being encoded in hex/base16 format
          const cognitoCustomState = cognitoState.split('-')[1];
          const continueUrl = hexToAscii(cognitoCustomState);
          navigate(continueUrl);
        } else if (userHasPermissionOnResource(PermissionsTypes.Read, 'bookmark', user?.ownPermissions)) {
          navigate('/bookmarks/list', { replace: true });
        } else {
          const pathWithPermission = items.find((item) => (Array.isArray(item.mainResource)
            ? item.mainResource.some((resource) => userHasPermissionOnResource(PermissionsTypes.Read, resource, user?.ownPermissions))
            : userHasPermissionOnResource(PermissionsTypes.Read, item.mainResource, user?.ownPermissions)));
          if (pathWithPermission) {
            navigate(pathWithPermission.path, { replace: true });
          }
        }
      }
      setChecked(true);
    }
  }, [isLoading, user, isAuthenticated, location, searchParams, navigate]);

  if (!checked) {
    return null;
  }
  const pathConfig = items.find((item) => item.path === location.pathname);
  if (
    pathConfig
    && pathConfig.mainResource
    && Array.isArray(pathConfig.mainResource)
    && !pathConfig.mainResource.some((resource) => userHasPermissionOnResource(PermissionsTypes.Read, resource, user?.ownPermissions))) {
    return React.createElement(ForbiddenPage);
  }

  // If got here, it means that the redirect did not occur, and that tells us that the user is
  // authenticated / authorized.

  return children;
};
